import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  Row, Col, Card, Spin, Typography, Space, Tag, Modal,
  List, Button, Progress, Popover, Divider, Tooltip, Table,
} from 'antd';
import {
  ExperimentOutlined, CalendarOutlined, EnvironmentOutlined,
  WarningOutlined, CarOutlined, CheckCircleOutlined,
  ClockCircleOutlined, RiseOutlined, ArrowUpOutlined,
  LeftOutlined, RightOutlined, BarChartOutlined,
  QuestionCircleOutlined, CameraOutlined, MessageOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { dashboardAPI } from '../services/api';
import { brand, gray, state, font } from '../theme/tokens';
import { EmptyState } from '../components/common';
import { useFillHeight } from '../hooks/useViewportFit';
import dayjs from 'dayjs';

declare global { interface Window { naver: any; } }

const { Text } = Typography;

/* ─── 한국 주요 도시 좌표 ─── */
const CITY_COORDS: Record<string, [number, number]> = {
  '서울': [37.5665, 126.9780], '부산': [35.1796, 129.0756], '대구': [35.8714, 128.6014],
  '인천': [37.4563, 126.7052], '광주': [35.1595, 126.8526], '대전': [36.3504, 127.3845],
  '울산': [35.5384, 129.3114], '세종': [36.4800, 127.0000],
  '수원': [37.2636, 127.0286], '용인': [37.2411, 127.1776], '안성': [37.0078, 127.2798],
  '이천': [37.2719, 127.4353], '평택': [36.9922, 127.1128], '화성': [37.1994, 126.8313],
  '파주': [37.7599, 126.7806], '양평': [37.4919, 127.4875],
  '천안': [36.8151, 127.1139], '서산': [36.7846, 126.4503], '논산': [36.1870, 127.0987],
  '보령': [36.3334, 126.6129], '당진': [36.8897, 126.6459],
  '청주': [36.6424, 127.4890], '제천': [37.1328, 128.1910], '진천': [36.8555, 127.4362],
  '음성': [36.9400, 127.6900], '충주': [36.9910, 127.9259], '괴산': [36.8153, 127.7866],
  '옥천': [36.3060, 127.5712],
  '춘천': [37.8813, 127.7298], '원주': [37.3422, 127.9202], '강릉': [37.7519, 128.8761],
  '정선': [37.3809, 128.6609], '삼척': [37.4499, 129.1655], '횡성': [37.4917, 127.9847],
  '영월': [37.1838, 128.4618],
  '전주': [35.8242, 127.1480], '군산': [35.9676, 126.7369], '익산': [35.9483, 126.9577],
  '나주': [35.0154, 126.7107], '순천': [34.9508, 127.4873], '목포': [34.8118, 126.3922],
  '진주': [35.1800, 128.1076], '창원': [35.2281, 128.6812], '김해': [35.2285, 128.8894],
  '거창': [35.6872, 127.9096], '밀양': [35.5039, 128.7464],
  '안동': [36.5684, 128.7228], '군위': [36.2428, 128.5730], '상주': [36.4109, 128.1590],
  '영주': [36.8057, 128.6239],
  '서귀포': [33.2541, 126.5600], '제주': [33.4996, 126.5312],
};
const getCoords = (region: string, city: string): [number, number] | null => {
  if (CITY_COORDS[city]) return CITY_COORDS[city];
  const regionCenters: Record<string, [number, number]> = {
    '서울': [37.5665, 126.9780], '경기': [37.2750, 127.0095], '인천': [37.4563, 126.7052],
    '강원': [37.8228, 128.1555], '충북': [36.6357, 127.4913], '충남': [36.6588, 126.6728],
    '전북': [35.8203, 127.1088], '전남': [34.8161, 126.4629], '경북': [36.4919, 128.8889],
    '경남': [35.4606, 128.2132], '제주': [33.4890, 126.4983], '대전': [36.3504, 127.3845],
    '대구': [35.8714, 128.6014], '부산': [35.1796, 129.0756], '울산': [35.5384, 129.3114],
    '광주': [35.1595, 126.8526], '세종': [36.4800, 127.0000],
  };
  return regionCenters[region] || null;
};

const COLORS = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#f97316', '#14b8a6', '#e879f9', '#06b6d4'];

const CATEGORY_COLORS: Record<string, string> = {
  '살균':   '#f472b6', '살균제': '#f472b6',
  '살충':   '#10b981', '살충제': '#10b981',
  '제초제': '#facc15', '제초':   '#facc15',
};

/* 구분별 도넛 범례 표시 순서 — 살균제 → 살충제 → 제초제 → 기초시험 → 기타 */
const CATEGORY_LEGEND_ORDER = ['살균제', '살균', '살충제', '살충', '제초제', '제초', '기초시험'];
const categoryRank = (name: string) => {
  const i = CATEGORY_LEGEND_ORDER.indexOf(name);
  return i < 0 ? CATEGORY_LEGEND_ORDER.length : i;
};
/* 구분별 도넛 표기 통일 — DB 명칭(살균/살충/제초)을 '~제' 표기로 표시 */
const CATEGORY_DISPLAY_NAME: Record<string, string> = { '살균': '살균제', '살충': '살충제', '제초': '제초제' };
const categoryDisplayName = (name: string) => CATEGORY_DISPLAY_NAME[name] || name;

/* 상태 진행순서: 대기→처리중→처리완료→조사완료→성적서작성중→성적서완료→발급완료→최종종료 */
const STATUS_COLOR: Record<string, string> = {
  '대기':       '#94a3b8',
  '시작전':     '#cbd5e1',
  '처리중':     '#3b82f6',
  '진행중':     '#10b981',
  '처리완료':   '#6366f1',
  '조사완료':   '#f59e0b',
  '성적서작성중': '#8b5cf6',
  '성적서완료': '#10b981',
  '발급완료':   '#0ea5e9',
  '최종종료':   '#64748b',
  '완료':       '#059669',
  '중단':       '#ef4444',
};

/* 최종 완료 단계 (성적서 발급 이상) */
const COMPLETE_STATUSES = new Set(['성적서완료', '발급완료', '최종종료', '완료']);
/* 중단/종료 */
const FINAL_STATUSES    = new Set(['최종종료', '완료', '중단']);

interface Stats {
  total_protocols: number; total_treatments: number; total_schedules: number;
  total_companies: number; total_locations: number; total_researchers: number;
  schedules_this_year: number; active_schedules: number; completed_this_year: number;
  trips_this_month: number; trips_this_year: number;
  protocols_by_month: { month: string; count: number }[];
  institutions: { name: string; count: number }[];
  crops: { name: string; count: number }[];
  schedules_by_year: { name: string; count: number }[];
  schedules_by_category: { name: string; count: number }[];
  schedules_by_company: { name: string; count: number }[];
  schedules_by_location: { region: string; city: string; name: string; count: number }[];
  schedules_by_status: { name: string; count: number }[];
  schedules_by_year_status: { year: string; status: string; count: number }[];
  schedules_photo_by_category: { category: string; has_photo: boolean | null; count: number }[];
  schedules_by_month_this_year: { month: string; count: number }[];
  trips_by_month_this_year: { month: string; count: number }[];
  researcher_trip_stats: { name: string; count: number }[];
  recent_schedules: { id: number; test_number: string; title: string; status: string; company_name: string; test_year: string; created_at: string }[];
}

/* ───── KPI Card ───── */
const KpiCard: React.FC<{
  title: string; value: number | string; sub?: string;
  icon: React.ReactNode; color: string; bg: string; trend?: number;
  popover?: React.ReactNode;
}> = ({ title, value, sub, icon, color, bg, trend, popover }) => (
  <Card size="small" style={{ borderRadius: 14, border: 'none', background: bg, height: '100%' }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div>
        <div style={{ fontSize: 11, color: gray[500], marginBottom: 4 }}>{title}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ fontSize: 28, fontWeight: 900, color, lineHeight: 1.1 }}>{value}</div>
        </div>
        {/* keep-all: ? 가 우측으로 오면서 좁아진 폭에서 '미지정' 같은 단어가 쪼개지는 것 방지 */}
        {sub && <div style={{ fontSize: 11, color: gray[400], marginTop: 4, wordBreak: 'keep-all' }}>{sub}</div>}
        {trend !== undefined && (
          <div style={{ fontSize: 11, color: trend >= 0 ? state.success : state.error, marginTop: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
            <ArrowUpOutlined style={{ transform: trend < 0 ? 'rotate(180deg)' : undefined }} />
            전월 대비 {Math.abs(trend)}건
          </div>
        )}
      </div>
      {/* ? 는 우측 상단에 우측정렬 — 기존 아이콘 바로 왼쪽 (파트너 대시보드와 동일 규칙) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {popover && (
          <Popover content={popover} trigger="hover" placement="bottomRight" arrow={false}>
            <QuestionCircleOutlined style={{ fontSize: 14, color: gray[300], cursor: 'pointer' }} />
          </Popover>
        )}
        <div style={{ width: 40, height: 40, borderRadius: 10, background: color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color }}>
          {icon}
        </div>
      </div>
    </div>
  </Card>
);

/* ───── CardTip: 카드 제목 옆 ? 툴팁 ───── */
const CardTip: React.FC<{ content: React.ReactNode }> = ({ content }) => (
  <Tooltip
    title={<div style={{ fontSize: 12, lineHeight: 1.75 }}>{content}</div>}
    overlayStyle={{ maxWidth: 300 }}
    placement="bottomLeft"
  >
    <QuestionCircleOutlined style={{ fontSize: 13, color: gray[300], cursor: 'pointer' }} />
  </Tooltip>
);

/* ───── Section Label ───── */
const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
    <div style={{ width: 3, height: 14, background: brand.primaryHover, borderRadius: 2 }} />
    <Text style={{ fontSize: 11, fontWeight: 700, color: gray[400], letterSpacing: '0.06em', textTransform: 'uppercase' }}>
      {children}
    </Text>
  </div>
);

const DashboardPage: React.FC = () => {
  /* 페이지를 뷰포트 바닥까지 실측한 높이로 고정 — 문서 세로스크롤 방지.
     가변 섹션(진행 현황)이 남는 공간을 차지하고 넘치면 그 안에서만 스크롤한다. */
  const { ref: fillRef, height: fillHeight } = useFillHeight(480);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [alertModalOpen, setAlertModalOpen] = useState(false);

  // 파트너 문의/요청 (답변 대기) — 로그인 첫 화면 알림
  const [inquiryAlerts, setInquiryAlerts] = useState<any[]>([]);
  const [inquiryModalOpen, setInquiryModalOpen] = useState(false);

  const [upcomingYear, setUpcomingYear] = useState<number>(dayjs().year());
  const [upcomingMonth, setUpcomingMonth] = useState<number>(dayjs().month() + 1);
  const [upcomingData, setUpcomingData] = useState<any>(null);
  const [upcomingLoading, setUpcomingLoading] = useState(false);

  // 구분별 사진등록 현황 → 세부 목록 모달
  const [photoModal, setPhotoModal] = useState<{ open: boolean; title: string; rows: any[]; loading: boolean }>(
    { open: false, title: '', rows: [], loading: false });
  const PHOTO_LABEL: Record<string, string> = { yes: '사진 있음', no: '사진 없음', none: '미등록', all: '전체' };
  const openPhotoDetail = (categoryKey: string, categoryLabel: string, photo: 'yes' | 'no' | 'none' | 'all') => {
    setPhotoModal({ open: true, title: `${categoryLabel} · ${PHOTO_LABEL[photo]}`, rows: [], loading: true });
    dashboardAPI.photoSchedules(categoryKey, photo)
      .then(({ data }) => setPhotoModal(m => ({ ...m, rows: data, loading: false })))
      .catch(() => setPhotoModal(m => ({ ...m, rows: [], loading: false })));
  };

  // 진행상태별 현황 → 세부 목록 모달 (누적 바 구간 / 범례 칩 클릭 공용)
  const [statusModal, setStatusModal] = useState<{
    open: boolean; status: string; scope: string; rows: any[]; loading: boolean;
  }>({ open: false, status: '', scope: '', rows: [], loading: false });

  /** years 가 비어 있으면 전체 연도 집계(범례 칩), 값이 있으면 해당 시즌 구간(누적 바) */
  const openStatusDetail = (status: string, years: string[], scopeLabel: string) => {
    setStatusModal({ open: true, status, scope: scopeLabel, rows: [], loading: true });
    dashboardAPI.statusSchedules(status, years)
      .then(({ data }) => setStatusModal(m => ({ ...m, rows: data, loading: false })))
      .catch(() => setStatusModal(m => ({ ...m, rows: [], loading: false })));
  };

  useEffect(() => {
    dashboardAPI.stats()
      .then(({ data }) => setStats(data))
      .catch(() => {})
      .finally(() => setLoading(false));
    dashboardAPI.alerts()
      .then(({ data }) => {
        setAlerts(data);
        if (data.length > 0) setAlertModalOpen(true);
      })
      .catch(() => {});
    dashboardAPI.inquiryAlerts()
      .then(({ data }) => {
        setInquiryAlerts(data);
        if (data.length > 0) setInquiryModalOpen(true);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setUpcomingLoading(true);
    dashboardAPI.upcoming(upcomingYear, upcomingMonth)
      .then(({ data }) => setUpcomingData(data))
      .catch(() => setUpcomingData(null))
      .finally(() => setUpcomingLoading(false));
  }, [upcomingYear, upcomingMonth]);

  const handlePrevMonth = () => {
    if (upcomingMonth === 1) { setUpcomingYear(y => y - 1); setUpcomingMonth(12); }
    else setUpcomingMonth(m => m - 1);
  };
  const handleNextMonth = () => {
    if (upcomingMonth === 12) { setUpcomingYear(y => y + 1); setUpcomingMonth(1); }
    else setUpcomingMonth(m => m + 1);
  };

  /* ── 구분별 도넛 ── */
  const categoryOption = useMemo(() => stats ? ({
    tooltip: { trigger: 'item' as const, textStyle: { fontSize: 11 },
      formatter: (p: any) => `${p.name}<br/>${p.value}건 (${p.percent}%)` },
    legend: {
      bottom: 0, itemWidth: 10, itemHeight: 10,
      textStyle: { fontSize: 11 },
      formatter: (name: string) => {
        const item = stats.schedules_by_category.find(d => categoryDisplayName(d.name) === name);
        return item ? `${name}  ${item.count}건` : name;
      },
    },
    series: [{
      type: 'pie', radius: ['42%', '68%'],
      center: ['50%', '40%'],
      label: { show: false },
      itemStyle: { borderRadius: 5, borderColor: '#fff', borderWidth: 2 },
      data: [...stats.schedules_by_category]
        .sort((a, b) => categoryRank(a.name) - categoryRank(b.name))
        .map((d, i) => ({
          name: categoryDisplayName(d.name), value: d.count,
          itemStyle: { color: CATEGORY_COLORS[d.name] || COLORS[i % COLORS.length] },
        })),
    }],
  }) : {}, [stats]);

  /* ── 월별 시험·출장 추이 ── */
  const monthlyTrendOption = useMemo(() => stats ? ({
    tooltip: {
      trigger: 'axis' as const,
      axisPointer: { type: 'shadow' as const },
      textStyle: { fontSize: 11 },
    },
    legend: {
      data: ['시험 개시', '출장'],
      bottom: 0, itemWidth: 12, itemHeight: 8,
      textStyle: { fontSize: 11, color: gray[500] },
    },
    grid: { top: 10, right: 4, bottom: 36, left: 0, containLabel: true },
    xAxis: {
      type: 'category' as const,
      data: stats.schedules_by_month_this_year.map(d => d.month),
      axisLabel: { fontSize: 10, color: gray[400] },
      axisTick: { show: false },
      axisLine: { lineStyle: { color: '#e2e8f0' } },
    },
    yAxis: {
      type: 'value' as const,
      minInterval: 1,
      axisLabel: { fontSize: 10, color: gray[400] },
      splitLine: { lineStyle: { color: '#f1f5f9', type: 'dashed' as const } },
    },
    series: [
      {
        name: '시험 개시',
        type: 'bar' as const,
        data: stats.schedules_by_month_this_year.map(d => d.count),
        itemStyle: { color: '#6366f1', borderRadius: [3, 3, 0, 0] },
        barMaxWidth: 22,
      },
      {
        name: '출장',
        type: 'line' as const,
        data: stats.trips_by_month_this_year.map(d => d.count),
        smooth: true,
        symbol: 'circle',
        symbolSize: 4,
        lineStyle: { color: '#f59e0b', width: 2 },
        itemStyle: { color: '#f59e0b' },
      },
    ],
  }) : {}, [stats]);

  /* ── 진행상태별 누적 가로 바 차트 ──
     마스터 상태코드(mst_status_codes) 순서 기준. 목록에 없는 상태도 뒤에 이어붙여 누락 방지 */
  const FIXED_STATUS_ORDER = [
    '시작전', '처리완료', '조사완료',
    '성적서작성중', '성적서완료', '발급완료', '최종종료',
  ];
  /* 연도코드 → 시즌 라벨 (시험일정 작기 필터·파트너 대시보드와 동일 표기 규칙)
     당해년도='N하', 과거='N~N+1동'(시작연도 기준), 미래='N-1~N동'(수확연도 등록분) */
  const seasonLabel = (yy: string, nowYY: string) => {
    if (!/^\d{2}$/.test(yy)) return yy;
    if (yy === nowYY) return `${yy}하`;
    const n = parseInt(yy, 10);
    return n < parseInt(nowYY, 10)
      ? `${yy}~${String(n + 1).padStart(2, '0')}동`
      : `${String(n - 1).padStart(2, '0')}~${yy}동`;
  };
  const orderedStatusNames = (names: string[]) => [
    ...FIXED_STATUS_ORDER,
    ...names.filter(n => !FIXED_STATUS_ORDER.includes(n)).sort(),
  ];
  /* 연도구분별 행: 연도 라벨 + 총건수 + 해당 연도 누적 바 (당해년도='하', 그 외='동') */
  const statusYearRows = useMemo(() => {
    if (!stats) return [];
    const yy0 = String(dayjs().year()).slice(-2);
    const byYear = new Map<string, Map<string, number>>();
    (stats.schedules_by_year_status || []).forEach(r => {
      const yy = /^\d{2,4}$/.test(r.year) ? r.year.slice(-2) : r.year;
      if (!byYear.has(yy)) byYear.set(yy, new Map());
      const m = byYear.get(yy)!;
      m.set(r.status, (m.get(r.status) || 0) + r.count);
    });
    const buckets = Array.from(byYear.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([yy, m]) => ({
        label: seasonLabel(yy, yy0),
        m,
        years: [yy],           // 세부 목록 조회 시 서버에 넘길 실제 연도
      }));
    /* 연도 누적 대비: 최근 3개 시즌만 개별 행, 그 이전은 '이전 시즌 누계' 1행으로 합산 (행 수 최대 4 고정) */
    let display = buckets;
    if (buckets.length > 4) {
      const older = buckets.slice(0, buckets.length - 3);
      const merged = new Map<string, number>();
      older.forEach(b => b.m.forEach((c, name) => merged.set(name, (merged.get(name) || 0) + c)));
      display = [
        // 누계 행은 여러 연도를 합친 것이므로 years 도 전부 합쳐 넘긴다
        { label: `~${older[older.length - 1].label} 누계`, m: merged, years: older.flatMap(b => b.years) },
        ...buckets.slice(-3),
      ];
    }
    return display.map(({ label, m, years }) => {
        const total = Math.max(Array.from(m.values()).reduce((s, c) => s + c, 0), 1);
        const visible = orderedStatusNames(Array.from(m.keys()))
          .map(name => ({ name, count: m.get(name) ?? 0 }))
          .filter(s => s.count > 0);
        const option = {
          tooltip: {
            trigger: 'item' as const,
            textStyle: { fontSize: 11 },
            formatter: (params: any) =>
              `<b>${params.seriesName}</b><br/>${params.value}건 / ${label} ${total}건 (${Math.round((params.value as number) / total * 100)}%)`,
          },
          grid: { top: 2, bottom: 2, left: 0, right: 0 },
          xAxis: { type: 'value' as const, show: false, max: total },
          yAxis: { type: 'category' as const, data: [''], show: false },
          series: visible.map((s, idx) => ({
            name: s.name,
            type: 'bar' as const,
            stack: 'total',
            barWidth: 16,
            data: [s.count],
            itemStyle: {
              color: STATUS_COLOR[s.name] || '#94a3b8',
              borderRadius:
                visible.length === 1 ? [4, 4, 4, 4]
                : idx === 0 ? [4, 0, 0, 4]
                : idx === visible.length - 1 ? [0, 4, 4, 0]
                : [0, 0, 0, 0],
            },
            label: {
              show: s.count / total > 0.07,
              position: 'inside' as const,
              formatter: `${s.count}`,
              color: '#fff',
              fontSize: 9,
              fontWeight: 700,
            },
          })),
        };
        // segments: 누적 바 위에 얹을 클릭 오버레이용 (ECharts canvas 는 클릭 이벤트가
        // 안정적으로 잡히지 않아, 사진등록 현황과 동일하게 HTML 요소로 클릭을 받는다)
        return { label, total, option, years, segments: visible };
      });
  }, [stats]);

  /* ── Naver Map ── */
  const mapMarkers = useMemo(() => {
    if (!stats) return [];
    return stats.schedules_by_location
      .map(loc => ({ ...loc, coords: getCoords(loc.region, loc.city) }))
      .filter(loc => loc.coords !== null);
  }, [stats]);
  const maxCount = useMemo(() => Math.max(...mapMarkers.map(m => m.count), 1), [mapMarkers]);
  const naverMapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);

  const initNaverMap = useCallback(() => {
    if (!naverMapRef.current || !window.naver?.maps) return;
    markersRef.current.forEach(({ marker, infoWindow, tooltip }) => {
      marker.setMap(null); infoWindow.close(); if (tooltip) tooltip.close();
    });
    markersRef.current = [];
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new window.naver.maps.Map(naverMapRef.current, {
        center: new window.naver.maps.LatLng(36.0, 127.8), zoom: 7, minZoom: 6, maxZoom: 16,
        zoomControl: true, zoomControlOptions: { position: window.naver.maps.Position.TOP_RIGHT, style: window.naver.maps.ZoomControlStyle.SMALL },
      });
    }
    const map = mapInstanceRef.current;
    mapMarkers.forEach(m => {
      const coords = m.coords as [number, number];
      const position = new window.naver.maps.LatLng(coords[0], coords[1]);
      const radius = Math.max(8, (m.count / maxCount) * 28);
      const marker = new window.naver.maps.Marker({
        position, map,
        icon: {
          content: `<div style="width:${radius * 2}px;height:${radius * 2}px;border-radius:50%;background:rgba(99,102,241,0.6);border:2px solid #4338ca;box-shadow:0 2px 8px rgba(99,102,241,0.35);display:flex;align-items:center;justify-content:center;font-size:${Math.max(10, radius * 0.7)}px;font-weight:700;color:#fff;cursor:pointer;">${m.count}</div>`,
          anchor: new window.naver.maps.Point(radius, radius),
        },
      });
      const infoWindow = new window.naver.maps.InfoWindow({
        content: `<div style="padding:10px 14px;min-width:120px;border-radius:8px;font-family:Pretendard,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;"><div style="font-weight:700;font-size:14px;color:#1e293b;margin-bottom:4px;">${m.name}</div><div style="font-size:12px;color:#64748b;">${m.region} ${m.city}</div><div style="font-size:13px;color:#4f46e5;font-weight:600;margin-top:4px;">시험 ${m.count}건</div></div>`,
        borderWidth: 1, borderColor: '#e2e8f0', backgroundColor: '#fff',
        anchorSize: new window.naver.maps.Size(10, 10), anchorColor: '#fff',
      });
      const tooltip = new window.naver.maps.InfoWindow({
        content: `<div style="padding:6px 12px;white-space:nowrap;font-family:Pretendard,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;font-size:12px;font-weight:600;color:#1e293b;">${m.name} <span style="color:#4f46e5;">${m.count}건</span></div>`,
        borderWidth: 1, borderColor: '#e2e8f0', backgroundColor: '#fff',
        anchorSize: new window.naver.maps.Size(8, 8), anchorColor: '#fff',
        pixelOffset: new window.naver.maps.Point(0, -4),
      });
      window.naver.maps.Event.addListener(marker, 'mouseover', () => tooltip.open(map, marker));
      window.naver.maps.Event.addListener(marker, 'mouseout', () => tooltip.close());
      window.naver.maps.Event.addListener(marker, 'click', () => {
        tooltip.close();
        infoWindow.getMap() ? infoWindow.close() : infoWindow.open(map, marker);
      });
      markersRef.current.push({ marker, infoWindow, tooltip });
    });
  }, [mapMarkers, maxCount]);

  useEffect(() => {
    if (!stats) return;
    if (window.naver?.maps) { initNaverMap(); return; }
    const check = setInterval(() => { if (window.naver?.maps) { clearInterval(check); initNaverMap(); } }, 200);
    return () => clearInterval(check);
  }, [stats, initNaverMap]);

  useEffect(() => {
    const el = naverMapRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      if (mapInstanceRef.current && el.offsetWidth > 0) {
        const map = mapInstanceRef.current;
        const center = map.getCenter();
        window.naver?.maps && map.setSize(new window.naver.maps.Size(el.offsetWidth, el.offsetHeight));
        map.setCenter(center);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  if (!stats) return null;

  const thisYear = dayjs().year();
  /* 완료율: 전체 시즌 기준 (완료 KPI·분모 모두 전 시즌) */
  const completionRate = stats.total_schedules > 0
    ? Math.round((stats.completed_this_year / stats.total_schedules) * 100) : 0;
  /* ── 연도별 시험 건수 — 전 연도 동적 집계 (진행상태별 전체 건수와 합계 일치) ──
     시즌 라벨: seasonLabel() — 당해년도='26하', 과거='25~26동', 미래='26~27동' */
  const nowYY = String(thisYear).slice(-2);
  const yearBuckets = (() => {
    const map = new Map<string, number>();
    (stats.schedules_by_year || []).forEach(d => {
      const yy = /^\d{2,4}$/.test(d.name) ? d.name.slice(-2) : d.name;
      map.set(yy, (map.get(yy) || 0) + d.count);
    });
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([yy, count]) => ({
        label: seasonLabel(yy, nowYY),
        count,
      }));
  })();
  const totalYearSum = yearBuckets.reduce((s, d) => s + d.count, 0);
  const maxYear = Math.max(...yearBuckets.map(d => d.count), 1);

  /* ── 구분별(살균·살충·제초) 성적서사진 등록 현황 ── */
  const photoRows = [
    { key: '살균', label: '살균제' },
    { key: '살충', label: '살충제' },
    { key: '제초', label: '제초제' },
  ].map(g => {
    const rows = (stats.schedules_photo_by_category || []).filter(r => r.category.startsWith(g.key));
    const yes = rows.filter(r => r.has_photo === true).reduce((s, r) => s + r.count, 0);
    const no = rows.filter(r => r.has_photo === false).reduce((s, r) => s + r.count, 0);
    const none = rows.filter(r => r.has_photo === null || r.has_photo === undefined).reduce((s, r) => s + r.count, 0);
    const total = yes + no + none;
    return { key: g.key, label: g.label, yes, no, none, total: Math.max(total, 1), rate: total ? Math.round((yes / total) * 100) : 0 };
  });

  /* ── 진행중 / 완료 시험 세부 팝오버 (schedules_by_status 기반 동적 집계) ── */
  const allStatuses = stats.schedules_by_status || [];

  // 진행중: FINAL_STATUSES 제외한 모든 상태
  const activeBreakdown = allStatuses
    .filter(s => !FINAL_STATUSES.has(s.name))
    .map(s => ({ ...s, color: STATUS_COLOR[s.name] || '#94a3b8' }));
  const maxActive = Math.max(...activeBreakdown.map(d => d.count), 1);

  // 완료: COMPLETE_STATUSES
  const completeBreakdown = allStatuses
    .filter(s => COMPLETE_STATUSES.has(s.name))
    .map(s => ({ ...s, color: STATUS_COLOR[s.name] || '#94a3b8' }));
  const maxComplete = Math.max(...completeBreakdown.map(d => d.count), 1);

  const mkBreakdownPopover = (
    title: string, desc: string,
    items: { name: string; count: number; color: string }[],
    maxVal: number,
  ) => (
    <div style={{ width: 240, padding: '4px 0' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: gray[700], marginBottom: 3 }}>{title}</div>
      <div style={{ fontSize: 11, color: gray[400], marginBottom: 10 }}>{desc}</div>
      {items.length === 0 ? (
        <div style={{ fontSize: 12, color: gray[300], textAlign: 'center', padding: '8px 0' }}>해당 없음</div>
      ) : items.map(({ name, count, color }) => (
        <div key={name} style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color }}>{name}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: gray[700] }}>{count}건</span>
          </div>
          <div style={{ height: 5, background: gray[100], borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.round((count / maxVal) * 100)}%`, background: color, borderRadius: 3 }} />
          </div>
        </div>
      ))}
      <Divider style={{ margin: '8px 0' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12, color: gray[500] }}>합계</span>
        <span style={{ fontSize: 13, fontWeight: 800, color: gray[800] }}>{items.reduce((s, d) => s + d.count, 0)}건</span>
      </div>
    </div>
  );

  const activeBreakdownPopover = mkBreakdownPopover(
    '진행중 시험 세부 현황',
    '최종종료·완료·중단을 제외한 현재 진행 중인 시험 건수를 상태별로 표시합니다.',
    activeBreakdown, maxActive,
  );
  const completeBreakdownPopover = mkBreakdownPopover(
    '완료 시험 세부 현황',
    '성적서완료 단계 이상(성적서완료·발급완료·최종종료·완료)인 시험 건수를 표시합니다.',
    completeBreakdown, maxComplete,
  );

  const yearBreakdownPopover = (
    <div style={{ width: 220, padding: '4px 0' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: gray[700], marginBottom: 3 }}>연도별 시험 건수</div>
      <div style={{ fontSize: 11, color: gray[400], marginBottom: 10 }}>전체 등록된 시험일정의 연도별 건수입니다.</div>
      {yearBuckets.map((b, i) => ({ label: b.label, count: b.count, color: COLORS[i % COLORS.length] })).map(({ label, count, color }) => (
        <div key={label} style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color }}>{label}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: gray[700] }}>{count}건</span>
          </div>
          <div style={{ height: 6, background: gray[100], borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.round((count / maxYear) * 100)}%`, background: color, borderRadius: 3 }} />
          </div>
        </div>
      ))}
      <Divider style={{ margin: '8px 0' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12, color: gray[500] }}>합계</span>
        <span style={{ fontSize: 13, fontWeight: 800, color: gray[800] }}>{totalYearSum}건</span>
      </div>
    </div>
  );

  const tripPopover = (
    <div style={{ width: 230, padding: '4px 0' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: gray[700], marginBottom: 3 }}>이번달 출장·업무 현황</div>
      <div style={{ fontSize: 11, color: gray[400], marginBottom: 10 }}>
        이번달({dayjs().month() + 1}월) 등록된 업무/출장 전체 건수입니다.
      </div>
      <div style={{ fontSize: 11, color: gray[500], lineHeight: 1.9 }}>
        <div>· <b>외근</b>: 외근 · 오전외근 · 오후외근</div>
        <div>· <b>내근</b>: 내근 · 오전내근 · 오후내근</div>
        <div>· <b>휴가</b>: 연차 · 오전반차 · 오후반차</div>
      </div>
      <Divider style={{ margin: '8px 0' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12, color: gray[500] }}>올해 전체</span>
        <span style={{ fontSize: 13, fontWeight: 800, color: gray[800] }}>{stats.trips_this_year}건</span>
      </div>
    </div>
  );

  return (
    /* 실측 높이 고정 + 내부 폴백 스크롤 — 최소높이 미달 화면에서도 문서 스크롤은 생기지 않는다 */
    <div ref={fillRef} style={{ height: fillHeight, display: 'flex', flexDirection: 'column', overflowY: 'auto', overflowX: 'hidden' }}>

      {/* ══ SECTION 1 · 핵심 지표 ══ */}
      <SectionLabel>핵심 지표 · 전체 시즌</SectionLabel>
      <Row gutter={[12, 12]} style={{ marginBottom: 12, flexShrink: 0 }}>
        <Col xs={12} sm={6} md={6}>
          <KpiCard title="전체 시험일정" value={totalYearSum}
            sub={yearBuckets.map(b => `${b.label} ${b.count}건`).join(' · ')}
            icon={<CalendarOutlined />} color="#6366f1" bg={brand.primarySoftBg}
            popover={yearBreakdownPopover} />
        </Col>
        <Col xs={12} sm={6} md={6}>
          <KpiCard title="진행중 시험" value={stats.active_schedules}
            sub="전체 시즌 · 최종종료/중단 제외"
            icon={<ClockCircleOutlined />} color={state.success} bg={state.successSoft}
            popover={activeBreakdownPopover} />
        </Col>
        <Col xs={12} sm={6} md={6}>
          <KpiCard title="완료 시험" value={stats.completed_this_year}
            sub={`전체 시즌 · 성적서완료 이상 · 완료율 ${completionRate}%`}
            icon={<CheckCircleOutlined />} color={state.info} bg={state.infoSoft}
            popover={completeBreakdownPopover} />
        </Col>
        <Col xs={12} sm={6} md={6}>
          <KpiCard title="이번달 출장" value={stats.trips_this_month}
            sub={`연간 ${stats.trips_this_year}건`}
            icon={<CarOutlined />} color={state.warning} bg={state.warningSoft}
            popover={tripPopover} />
        </Col>
      </Row>

      {/* ══ SECTION 2 · 진행예정 + 현황 ══
           가변 섹션 — 남는 공간을 모두 받고, 내용이 넘치면 이 안에서만 스크롤 */}
      <SectionLabel>진행 현황</SectionLabel>
      {/* overflowX hidden: Row 의 음수 gutter 마진이 가로 스크롤바를 만들지 않게 */}
      <div style={{ flex: '1 1 auto', minHeight: 200, overflowY: 'auto', overflowX: 'hidden', marginBottom: 12 }}>
      <Row gutter={[12, 12]}>

        {/* 월별 진행예정 시험 */}
        <Col xs={24} lg={16}>
          <Card
            size="small"
            style={{ borderRadius: 12, height: '100%' }}
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Space>
                  <CalendarOutlined style={{ color: '#6366f1' }} />
                  <span style={{ fontSize: 13, fontWeight: 600 }}>월별 진행예정 시험</span>
                </Space>
                <Space size={4}>
                  <CardTip content={
                    <>
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>월별 진행예정 시험 안내</div>
                      <div>시험일정총괄표의 <b>개시일</b>을 기준으로, 선택한 월에 해당하는 시험 목록을 상순·중순·하순으로 구분하여 표시합니다.</div>
                      <div style={{ marginTop: 6, color: '#fde68a' }}>· 개시일 예시: 9월 상순, 6월 중순</div>
                      <div style={{ color: '#86efac' }}>· 최종종료·중단 상태는 제외됩니다.</div>
                      <div style={{ marginTop: 6, opacity: 0.8 }}>◀ ▶ 버튼으로 월을 이동할 수 있습니다.</div>
                    </>
                  } />
                  <Button size="small" type="text" icon={<LeftOutlined />} onClick={handlePrevMonth} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: brand.primary, minWidth: 80, textAlign: 'center', display: 'inline-block' }}>
                    {upcomingYear}-{String(upcomingMonth).padStart(2, '0')}
                  </span>
                  <Button size="small" type="text" icon={<RightOutlined />} onClick={handleNextMonth} />
                </Space>
              </div>
            }
          >
            {upcomingLoading ? (
              <Spin size="small" style={{ display: 'block', margin: '24px auto' }} />
            ) : (
              <Row gutter={0}>
                {(['상순', '중순', '하순'] as const).map((period, pi) => {
                  const periodData = upcomingData?.periods?.find((p: any) => p.period === period);
                  const schedules: any[] = periodData?.schedules || [];
                  const periodColors = ['#6366f1', '#0ea5e9', '#10b981'];
                  const pc = periodColors[pi];
                  return (
                    <Col xs={24} md={8} key={period}
                      style={{ borderRight: pi < 2 ? '1px solid #f1f5f9' : 'none', padding: '0 12px 4px' }}>
                      {/* Period header */}
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '8px 0 8px', marginBottom: 8,
                        borderBottom: `2px solid ${pc}22`,
                      }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: pc }} />
                        <span style={{ fontSize: 13, fontWeight: 700, color: pc }}>{period}</span>
                        <Tag style={{
                          fontSize: 10, margin: 0, lineHeight: '16px', padding: '0 5px',
                          background: `${pc}15`, borderColor: `${pc}40`, color: pc,
                        }}>{schedules.length}건</Tag>
                      </div>
                      {schedules.length === 0 ? (
                        <EmptyState title="이달 일정이 없습니다" size="sm" />
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, maxHeight: 280, overflowY: 'auto' }}>
                          {schedules.map((s: any) => (
                            <div key={s.id} style={{
                              padding: '7px 10px',
                              background: gray[50],
                              borderRadius: 7,
                              borderLeft: `3px solid ${pc}`,
                              border: '1px solid #f0f0f0',
                              borderLeftColor: pc,
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                                <span style={{ fontSize: 12, fontWeight: 700, color: brand.primary }}>{s.test_number}</span>
                                <Tag
                                  color={s.status === '진행중' ? 'green' : s.status === '완료' ? 'blue' : s.status === '대기' ? 'gold' : 'default'}
                                  style={{ fontSize: 10, margin: 0, lineHeight: '16px', padding: '0 5px' }}
                                >{s.status}</Tag>
                              </div>
                              <div style={{ fontSize: 11, color: gray[700], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.title}</div>
                              <div style={{ fontSize: 10, color: gray[400], marginTop: 2, display: 'flex', justifyContent: 'space-between' }}>
                                <span>{s.company_name}{s.location_name ? ` · ${s.location_name}` : ''}</span>
                                {s.start_date && <span style={{ color: gray[300] }}>개시 {s.start_date}</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </Col>
                  );
                })}
              </Row>
            )}
          </Card>
        </Col>

        {/* 올해 진행 현황 */}
        <Col xs={24} lg={8}>
          <Card size="small" style={{ borderRadius: 12, height: '100%' }}
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Space>
                  <RiseOutlined style={{ color: '#6366f1' }} />
                  <span style={{ fontSize: 13, fontWeight: 600 }}>진행상태별 현황</span>
                </Space>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CardTip content={
                    <>
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>진행상태별 현황</div>
                      <div>시험일정을 연도구분별로 나눠 각 연도의 진행 상태 누적 바와 총건수를 표시합니다.</div>
                      <div style={{ marginTop: 6, color: '#93c5fd' }}>· 바 세그먼트 호버: 상태별 건수·비율 확인</div>
                      <div style={{ color: '#86efac' }}>· 하단 칩: 전체 연도 합산 상태별 건수 (최종종료·중단 포함)</div>
                    </>
                  } />
                  <span style={{ fontSize: 11, color: gray[400], fontWeight: 400 }}>
                    전체 <b style={{ color: gray[800], fontSize: 13 }}>{stats.total_schedules}</b>건
                  </span>
                </span>
              </div>
            }>
            {/* 연도구분별 누적 바 */}
            {statusYearRows.map(row => (
              <div key={row.label} style={{ marginBottom: 4 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 1 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: gray[600] }}>{row.label}</span>
                  <span style={{ fontSize: 11, color: gray[400] }}>
                    <b style={{ color: gray[700], fontSize: 12 }}>{row.total}</b>건
                  </span>
                </div>
                <div style={{ position: 'relative' }}>
                  <ReactECharts option={row.option} style={{ height: 24 }} />
                  {/* 클릭 오버레이 — 막대와 동일한 비율로 구간을 덮어 클릭을 받는다.
                      grid 의 좌우 여백이 0이라 컨테이너 폭 = 막대 폭이므로 비율이 그대로 맞는다. */}
                  <div style={{ position: 'absolute', inset: 0, display: 'flex' }}>
                    {row.segments.map(seg => (
                      <Tooltip
                        key={seg.name}
                        title={`${seg.name} ${seg.count}건 / ${row.label} ${row.total}건 (${Math.round(seg.count / row.total * 100)}%) — 클릭하여 세부 확인`}
                      >
                        <div
                          onClick={() => openStatusDetail(seg.name, row.years, row.label)}
                          style={{ width: `${(seg.count / row.total) * 100}%`, cursor: 'pointer' }}
                        />
                      </Tooltip>
                    ))}
                  </div>
                </div>
              </div>
            ))}
            {/* 범례 — 1~2줄 가로 칩 (0건 포함 항상 표시) */}
            {(() => {
              const dbMap = new Map((stats.schedules_by_status || []).map(s => [s.name, s.count]));
              return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px 6px', marginTop: 10 }}>
                  {orderedStatusNames(Array.from(dbMap.keys())).map(name => {
                    const count = dbMap.get(name) ?? 0;
                    const color = STATUS_COLOR[name] || '#94a3b8';
                    const isEmpty = count === 0;
                    return (
                      // 0건 칩은 열어도 빈 목록이라 클릭 대상에서 제외한다
                      <span
                        key={name}
                        onClick={isEmpty ? undefined : () => openStatusDetail(name, [], '전체 시즌')}
                        title={isEmpty ? undefined : `${name} 세부 목록 보기`}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '3px 8px', borderRadius: 20,
                          background: isEmpty ? gray[50] : `${color}18`,
                          border: `1px solid ${isEmpty ? gray[200] : color + '55'}`,
                          whiteSpace: 'nowrap',
                          cursor: isEmpty ? 'default' : 'pointer',
                          transition: 'box-shadow .15s, transform .15s',
                        }}
                        onMouseEnter={e => {
                          if (isEmpty) return;
                          e.currentTarget.style.boxShadow = `0 0 0 2px ${color}33`;
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.boxShadow = 'none';
                          e.currentTarget.style.transform = 'none';
                        }}
                      >
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: isEmpty ? gray[300] : color, flexShrink: 0, display: 'inline-block' }} />
                        <span style={{ fontSize: 11, fontWeight: 600, color: isEmpty ? gray[300] : gray[700] }}>{name}</span>
                        <span style={{ fontSize: 11, fontWeight: 800, color: isEmpty ? gray[300] : color }}>{count}건</span>
                      </span>
                    );
                  })}
                </div>
              );
            })()}
          </Card>
        </Col>
      </Row>

      </div>

      {/* ══ SECTION 3 · 분석 ══ */}
      <SectionLabel>월별 분석</SectionLabel>
      <Row gutter={[12, 12]} style={{ marginBottom: 12, flexShrink: 0 }}>

        {/* 월별 시험 · 출장 추이 */}
        <Col xs={24} lg={9}>
          <Card size="small" style={{ borderRadius: 12, height: '100%' }}
            title={
              <Space>
                <BarChartOutlined style={{ color: '#6366f1' }} />
                <span style={{ fontSize: 13, fontWeight: 600 }}>월별 시험 개시 · 출장 추이</span>
                <Tag style={{ fontSize: 10 }}>{thisYear}년</Tag>
              </Space>
            }
            extra={
              <CardTip content={
                <>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>월별 시험 개시·출장 추이</div>
                  <div>당해 연도 월별 시험 개시 건수(막대)와 출장 건수(선)의 추이를 동시에 표시합니다.</div>
                  <div style={{ marginTop: 6, color: '#a5b4fc' }}>· 막대: 해당 월 개시된 시험 건수</div>
                  <div style={{ color: '#6ee7b7' }}>· 선: 해당 월 등록된 출장 건수</div>
                </>
              } />
            }>
            <ReactECharts option={monthlyTrendOption} style={{ height: 190 }} />
          </Card>
        </Col>

        {/* 구분별 사진등록 현황 */}
        <Col xs={24} lg={7}>
          <Card size="small" style={{ borderRadius: 12, height: '100%' }}
            title={
              <Space>
                <CameraOutlined style={{ color: '#f59e0b' }} />
                <span style={{ fontSize: 13, fontWeight: 600 }}>구분별 사진등록 현황</span>
                <Tag style={{ fontSize: 10 }}>전체 시즌</Tag>
              </Space>
            }
            extra={
              <CardTip content={
                <>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>구분별 사진등록 현황</div>
                  <div>살균제·살충제·제초제 시험의 성적서사진유무 등록 상태를 표시합니다.</div>
                  <div style={{ marginTop: 6, color: '#86efac' }}>· 녹색: 있음 / 적색: 없음 / 회색: 미등록</div>
                  <div style={{ color: '#fde68a' }}>· 우측 %: 사진 있음 비율</div>
                  <div style={{ color: '#93c5fd' }}>· 막대 구간·구분명 클릭 시 세부 목록 확인</div>
                </>
              } />
            }>
            {photoRows.map(r => (
              <div key={r.label} style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                  <Tooltip title={`${r.label} 전체 목록 보기`}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: gray[600], cursor: 'pointer' }}
                      onClick={() => openPhotoDetail(r.key, r.label, 'all')}>{r.label}</span>
                  </Tooltip>
                  <span style={{ fontSize: 11, color: gray[400] }}>
                    있음 <b style={{ color: '#16a34a' }}>{r.yes}</b> / {r.total}건 · <b style={{ color: gray[700] }}>{r.rate}%</b>
                  </span>
                </div>
                <div style={{ display: 'flex', height: 14, borderRadius: 7, overflow: 'hidden', background: '#f1f5f9' }}>
                  {r.yes > 0 && <Tooltip title={`사진 있음 ${r.yes}건 — 클릭하여 세부 확인`}><div style={{ width: `${(r.yes / r.total) * 100}%`, background: '#22c55e', cursor: 'pointer' }} onClick={() => openPhotoDetail(r.key, r.label, 'yes')} /></Tooltip>}
                  {r.no > 0 && <Tooltip title={`사진 없음 ${r.no}건 — 클릭하여 세부 확인`}><div style={{ width: `${(r.no / r.total) * 100}%`, background: '#ef4444', cursor: 'pointer' }} onClick={() => openPhotoDetail(r.key, r.label, 'no')} /></Tooltip>}
                  {r.none > 0 && <Tooltip title={`미등록 ${r.none}건 — 클릭하여 세부 확인`}><div style={{ width: `${(r.none / r.total) * 100}%`, background: '#e2e8f0', cursor: 'pointer' }} onClick={() => openPhotoDetail(r.key, r.label, 'none')} /></Tooltip>}
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
              {[
                { label: '있음', color: '#22c55e' },
                { label: '없음', color: '#ef4444' },
                { label: '미등록', color: '#e2e8f0' },
              ].map(l => (
                <span key={l.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: gray[500] }}>
                  <span style={{ width: 8, height: 8, borderRadius: 4, background: l.color, display: 'inline-block' }} />{l.label}
                </span>
              ))}
            </div>
          </Card>
        </Col>

        {/* 구분별 현황 */}
        <Col xs={24} lg={8}>
          <Card size="small" style={{ borderRadius: 12 }}
            title={
              <Space>
                <ExperimentOutlined style={{ color: '#10b981' }} />
                <span style={{ fontSize: 13, fontWeight: 600 }}>구분별 시험 현황</span>
                <Tag style={{ fontSize: 10 }}>전체 시즌</Tag>
              </Space>
            }
            extra={
              <CardTip content={
                <>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>구분별 시험 현황</div>
                  <div>시험 구분(살균·살충·제초제 등 품목) 별 건수 비율을 도넛 차트로 표시합니다.</div>
                  <div style={{ marginTop: 6, color: '#86efac' }}>· 슬라이스 클릭 시 해당 구분 필터 가능</div>
                </>
              } />
            }>
            <ReactECharts option={categoryOption} style={{ height: 190 }} />
          </Card>
        </Col>
      </Row>

      {/* ══ SECTION 4 · 시험장소 지도 ══ */}
      <SectionLabel>시험장소 분포</SectionLabel>
      <Row gutter={[12, 12]} style={{ flexShrink: 0 }}>
        <Col xs={24}>
          <Card size="small" style={{ borderRadius: 12 }}
            title={
              <Space>
                <EnvironmentOutlined style={{ color: '#4f46e5' }} />
                <span style={{ fontSize: 13, fontWeight: 600 }}>시험장소 분포 지도</span>
                <Tag style={{ fontSize: 10 }}>전체 시즌 · {mapMarkers.length}개 지역</Tag>
              </Space>
            }
            extra={
              <CardTip content={
                <>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>시험장소 분포 지도</div>
                  <div>시험장소의 지역별 분포를 지도 위 버블로 표시합니다.</div>
                  <div style={{ marginTop: 6, color: '#a5b4fc' }}>· 버블 크기: 해당 지역 시험 건수에 비례</div>
                  <div style={{ color: '#93c5fd' }}>· 버블 클릭: 장소명·건수 상세 확인</div>
                  <div style={{ color: '#fde68a' }}>· 마우스 오버: 간략 정보 표시</div>
                </>
              } />
            }>
            <div ref={naverMapRef} style={{ height: 200, borderRadius: 10, overflow: 'hidden' }} />
          </Card>
        </Col>
      </Row>

      {/* ── 파트너 문의/요청 알림 모달 ── */}
      <Modal
        open={inquiryModalOpen}
        onCancel={() => setInquiryModalOpen(false)}
        footer={<Button type="primary" onClick={() => setInquiryModalOpen(false)}>확인</Button>}
        width={560}
        title={
          <Space>
            <MessageOutlined style={{ color: brand.primary, fontSize: 18 }} />
            <span style={{ fontWeight: 700, fontSize: 15, color: brand.primary }}>파트너 문의/요청 도착</span>
            <Tag color="orange">{inquiryAlerts.length}건</Tag>
          </Space>
        }
      >
        <div style={{ marginBottom: 12, fontSize: 13, color: gray[500] }}>
          답변 대기 중인 파트너사 문의/요청입니다. 좌측 메뉴 <b>운영관리 &gt; 파트너 문의/요청</b>에서 답변할 수 있습니다.
        </div>
        <div style={{ maxHeight: 460, overflowY: 'auto', paddingRight: 4 }}>
          <List
            size="small" dataSource={inquiryAlerts}
            renderItem={(item: any) => (
              <List.Item style={{ padding: '10px 0' }}>
                <div style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, gap: 8 }}>
                    <Space size={6} wrap>
                      <Tag color={item.inquiry_type === '요청' ? 'purple' : 'blue'} style={{ fontWeight: 600 }}>
                        {item.inquiry_type}
                      </Tag>
                      {item.company_name && <Tag color="blue" style={{ fontWeight: 600 }}>{item.company_name}</Tag>}
                      {item.test_number && (
                        <span style={{ fontWeight: 700, color: brand.primary, fontSize: 13 }}>{item.test_number}</span>
                      )}
                    </Space>
                    <Tag color="orange" style={{ fontSize: 11 }}>답변대기</Tag>
                  </div>
                  <div style={{ fontSize: 12, color: gray[700], fontWeight: 600, marginBottom: 4 }}>{item.title}</div>
                  <div style={{ display: 'flex', gap: 12, fontSize: 11, color: gray[400] }}>
                    <span>작성자: <b style={{ color: gray[700] }}>{item.partner_username}</b></span>
                    <span>등록: {(item.created_at || '').replace('T', ' ').slice(0, 16)}</span>
                  </div>
                </div>
              </List.Item>
            )}
          />
        </div>
      </Modal>

      {/* ── 개시일 경과 알림 모달 ── */}
      <Modal
        open={alertModalOpen}
        onCancel={() => setAlertModalOpen(false)}
        footer={<Button type="primary" danger onClick={() => setAlertModalOpen(false)}>확인</Button>}
        width={520}
        title={
          <Space>
            <WarningOutlined style={{ color: state.error, fontSize: 18 }} />
            <span style={{ fontWeight: 700, fontSize: 15, color: state.error }}>정식시기 미등록 알림</span>
            <Tag color="red">{alerts.length}건</Tag>
          </Space>
        }
      >
        <div style={{ marginBottom: 12, fontSize: 13, color: gray[500] }}>
          개시일이 경과했지만 1차 처리일자가 등록되지 않은 시험입니다.
        </div>
        <div style={{ maxHeight: 504, overflowY: 'auto', paddingRight: 4 }}>
          <List
            size="small" dataSource={alerts}
            renderItem={(item: any) => (
              <List.Item style={{ padding: '10px 0' }}>
                <div style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <Space size={6}>
                      {item.company_name && <Tag color="blue" style={{ fontWeight: 600 }}>{item.company_name}</Tag>}
                      <span style={{ fontWeight: 700, color: brand.primary, fontSize: 13 }}>{item.test_number}</span>
                    </Space>
                    <Tag color="red" style={{ fontSize: 11 }}>+{item.overdue_days}일 경과</Tag>
                  </div>
                  <div style={{ fontSize: 12, color: gray[700], marginBottom: 4 }}>{item.title}</div>
                  <div style={{ display: 'flex', gap: 12, fontSize: 11, color: gray[400] }}>
                    <span>개시일: <b style={{ color: state.error }}>{item.start_date}</b></span>
                    <span>상태: <b style={{ color: gray[700] }}>{item.status}</b></span>
                  </div>
                </div>
              </List.Item>
            )}
          />
        </div>
      </Modal>

      {/* ── 구분별 사진등록 세부 목록 모달 ── */}
      <Modal
        open={photoModal.open}
        onCancel={() => setPhotoModal(m => ({ ...m, open: false }))}
        footer={<Button onClick={() => setPhotoModal(m => ({ ...m, open: false }))}>닫기</Button>}
        width={860}
        title={
          <Space>
            <CameraOutlined style={{ color: '#f59e0b' }} />
            <span style={{ fontWeight: 700, fontSize: 15 }}>사진등록 세부 목록</span>
            <Tag color="gold">{photoModal.title}</Tag>
            {!photoModal.loading && <Tag>{photoModal.rows.length}건</Tag>}
          </Space>
        }
      >
        <Table
          dataSource={photoModal.rows}
          rowKey="id"
          size="small"
          loading={photoModal.loading}
          pagination={{ pageSize: 10, showTotal: (t) => `총 ${t}건`, size: 'small' }}
          columns={[
            { title: '시험번호', dataIndex: 'test_number', width: 140,
              render: (v: string) => <span style={{ fontWeight: 700, color: brand.primary, fontSize: 12 }}>{v}</span> },
            { title: '항목명', dataIndex: 'title', ellipsis: true,
              render: (v: string) => <Tooltip title={v}>{v}</Tooltip> },
            { title: '의뢰사', dataIndex: 'company_name', width: 90,
              render: (v: string) => v ? <Tag color="blue" style={{ fontWeight: 600 }}>{v}</Tag> : '-' },
            { title: '구분', dataIndex: 'category_name', width: 70 },
            { title: '년도', dataIndex: 'test_year', width: 55, align: 'center' as const },
            { title: '상태', dataIndex: 'status', width: 92,
              render: (v: string) => <Tag color={STATUS_COLOR[v] || '#94a3b8'} style={{ fontSize: 11 }}>{v || '-'}</Tag> },
            { title: '사진', dataIndex: 'has_result_photo', width: 74, align: 'center' as const,
              render: (v: boolean | null) => v === true
                ? <Tag color="green" style={{ fontWeight: 600 }}>있음</Tag>
                : v === false
                  ? <Tag color="red" style={{ fontWeight: 600 }}>없음</Tag>
                  : <Tag style={{ color: '#94a3b8' }}>미등록</Tag> },
          ]}
          locale={{ emptyText: '해당 조건의 시험이 없습니다' }}
        />
      </Modal>

      {/* ── 진행상태별 세부 목록 모달 ── */}
      <Modal
        open={statusModal.open}
        onCancel={() => setStatusModal(m => ({ ...m, open: false }))}
        footer={<Button onClick={() => setStatusModal(m => ({ ...m, open: false }))}>닫기</Button>}
        width={900}
        title={
          <Space>
            <CheckCircleOutlined style={{ color: STATUS_COLOR[statusModal.status] || '#6366f1' }} />
            <span style={{ fontWeight: 700, fontSize: 15 }}>진행상태 세부 목록</span>
            <Tag color={STATUS_COLOR[statusModal.status] || '#94a3b8'} style={{ fontWeight: 700 }}>
              {statusModal.status || '-'}
            </Tag>
            <Tag>{statusModal.scope}</Tag>
            {!statusModal.loading && <Tag>{statusModal.rows.length}건</Tag>}
          </Space>
        }
      >
        <Table
          dataSource={statusModal.rows}
          rowKey="id"
          size="small"
          loading={statusModal.loading}
          pagination={{ pageSize: 10, showTotal: (t) => `총 ${t}건`, size: 'small' }}
          columns={[
            { title: '시험번호', dataIndex: 'test_number', width: 140,
              render: (v: string) => <span style={{ fontWeight: 700, color: brand.primary, fontSize: 12 }}>{v}</span> },
            { title: '항목명', dataIndex: 'title', ellipsis: true,
              render: (v: string) => <Tooltip title={v}>{v}</Tooltip> },
            { title: '의뢰사', dataIndex: 'company_name', width: 90,
              render: (v: string) => v ? <Tag color="blue" style={{ fontWeight: 600 }}>{v}</Tag> : '-' },
            { title: '구분', dataIndex: 'category_name', width: 70 },
            { title: '시험장소', dataIndex: 'location_name', width: 110, ellipsis: true,
              render: (v: string) => v ? <Tooltip title={v}>{v}</Tooltip> : '-' },
            { title: '년도', dataIndex: 'test_year', width: 55, align: 'center' as const },
            { title: '개시일', dataIndex: 'start_date', width: 90, align: 'center' as const,
              render: (v: string) => v || '-' },
            { title: '상태', dataIndex: 'status', width: 92,
              render: (v: string) => <Tag color={STATUS_COLOR[v] || '#94a3b8'} style={{ fontSize: 11 }}>{v || '-'}</Tag> },
          ]}
          locale={{ emptyText: '해당 조건의 시험이 없습니다' }}
        />
      </Modal>
    </div>
  );
};

export default DashboardPage;
